import { REWARDS, getStageBossBalance } from "./constants";
import { getCareerStage, getStageBoss, isBossSeriesFinished } from "./clubs";
import {
  acquireReward,
  buildPendingMatch,
  canUseCard,
  clamp,
  consumeCard,
  getCareerFinale,
  getCareerMoveOutcome,
  getInventoryCount,
  getRelicEffectAmount,
  getRelicStackBonus,
} from "./engine";
import type { EncounterKind, GameAction, GameStateV2, MatchOutcome } from "./types";

function closeCurrentStage(
  state: GameStateV2,
  outcome: "completed" | "eliminated",
  summary: string,
): GameStateV2["careerHistory"] {
  if (state.careerHistory.some((entry) => entry.stage === state.season)) return state.careerHistory;
  return [...state.careerHistory, {
    stage: state.season,
    stageName: getCareerStage(state.season, state.finaleKind).name,
    team: state.season === 5 && state.finaleKind === "world-cup" ? state.player.country : state.club,
    appearances: Math.max(0, state.appearances - state.stageBaseline.appearances),
    minutesPlayed: Math.max(0, state.minutesPlayed - state.stageBaseline.minutesPlayed),
    goals: Math.max(0, state.goals - state.stageBaseline.goals),
    assists: Math.max(0, state.assists - state.stageBaseline.assists),
    ovrStart: state.stageBaseline.ovr,
    ovrEnd: state.ovr,
    titlesWon: Math.max(0, state.titles - state.stageBaseline.titles),
    outcome,
    summary,
  }];
}

function appendLog(log: string[], message?: string): string[] {
  return message ? [message, ...log].slice(0, 12) : log;
}

function applyMatchResult(
  state: GameStateV2,
  outcome: MatchOutcome,
  goals = 0,
  assists = 0,
  appearances = 1,
  minutesPlayed = 90,
  wins?: number,
  ovrGainOverride?: number,
  reputationGain = 0,
  streakOutcomes?: readonly (MatchOutcome | "draw")[],
): GameStateV2 {
  const pending = state.pendingMatch;
  if (!pending) return state;
  const won = outcome === "win";
  const braceletBonus = getRelicStackBonus(state.inventory, "brazalete");
  const resultMorale = won ? 5 : -12;
  const derbyPressure = pending.encounterKind === "derby" ? (won ? 10 : -4) : 0;
  const derbyPhysicalBonus = pending.encounterKind === "derby" && won ? 12 : 0;
  let moraleDelta = resultMorale + derbyPressure + braceletBonus;
  const partidoAPartido = REWARDS["partido-a-partido"].effect;
  if (!won && partidoAPartido.type === "lowMoraleLossRecovery" && getInventoryCount(state.inventory, "partido-a-partido") > 0 && state.morale < partidoAPartido.threshold) {
    moraleDelta = Math.min(0, moraleDelta + partidoAPartido.amount);
  }
  const resolvedStreakOutcomes = streakOutcomes?.length ? streakOutcomes : [outcome];
  let nextWinStreak = state.winStreak ?? 0;
  let bestWinStreak = state.bestWinStreak;
  let streakPhysicalRecovery = 0;
  const ritual = REWARDS["ritual-vestuario"].effect;
  for (const streakOutcome of resolvedStreakOutcomes) {
    nextWinStreak = streakOutcome === "win" ? nextWinStreak + 1 : 0;
    bestWinStreak = Math.max(bestWinStreak, nextWinStreak);
    if (streakOutcome === "win"
      && ritual.type === "streakPhysicalRecovery"
      && getInventoryCount(state.inventory, "ritual-vestuario") > 0
      && nextWinStreak >= ritual.streak) streakPhysicalRecovery += ritual.amount;
  }
  const winsEarned = Math.max(0, Math.floor(wins ?? (won ? 1 : 0)));
  const ovrGain = ovrGainOverride === undefined
    ? overallGainForResult(pending.encounterKind, outcome)
    : Math.max(0, Math.floor(ovrGainOverride));
  return {
    ...state,
    ovr: clamp(state.ovr + ovrGain, 0, 99),
    physical: clamp(state.physical + derbyPhysicalBonus + streakPhysicalRecovery),
    morale: clamp(state.morale + moraleDelta),
    clubReputation: clamp(state.clubReputation + reputationGain),
    appearances: (state.appearances ?? 0) + Math.max(0, Math.floor(appearances)),
    minutesPlayed: (state.minutesPlayed ?? 0) + Math.max(0, Math.floor(minutesPlayed)),
    goals: state.goals + Math.max(0, Math.floor(goals)),
    assists: state.assists + Math.max(0, Math.floor(assists)),
    wins: state.wins + winsEarned,
    derbyWins: state.derbyWins + (pending.encounterKind === "derby" && won ? 1 : 0),
    winStreak: nextWinStreak,
    bestWinStreak,
    pendingMatch: null,
  };
}

export function overallGainForResult(encounter: EncounterKind, outcome: MatchOutcome): number {
  if (encounter === "boss") return 0;
  if (encounter === "derby") return outcome === "win" ? 3 : 0;
  return outcome === "win" ? 2 : 0;
}

export function gameReducer(state: GameStateV2, action: GameAction): GameStateV2 {
  switch (action.type) {
    case "run/replaced":
      return action.state;

    case "reward/acquired":
      return { ...state, inventory: acquireReward(state.inventory, action.rewardId) };

    case "reward/consumed":
      return getInventoryCount(state.inventory, action.rewardId) > 0
        ? { ...state, inventory: consumeCard(state.inventory, action.rewardId) }
        : state;

    case "boss/reactionUsed": {
      if (action.resource === "physical") return { ...state, physical: clamp(state.physical - 12) };
      if (action.resource === "morale") return { ...state, morale: clamp(state.morale - 12) };
      if (!action.rewardId || getInventoryCount(state.inventory, action.rewardId) < 1) return state;
      const effect = REWARDS[action.rewardId].effect;
      return {
        ...state,
        physical: clamp(state.physical + (effect.type === "physicalRestore" ? effect.amount : 0)),
        morale: clamp(state.morale + (effect.type === "moraleRestore" ? effect.amount : 0)),
        inventory: consumeCard(state.inventory, action.rewardId),
      };
    }

    case "match/cardSelected": {
      if (action.rewardId === null) return { ...state, selectedCardId: null };
      const reward = REWARDS[action.rewardId];
      if (reward.kind !== "card" || (state.inventory[action.rewardId]?.count ?? 0) < 1) return state;
      return { ...state, selectedCardId: action.rewardId };
    }

    case "match/confirmed": {
      if (state.pendingMatch) return state;
      if (action.encounterKind === "boss" && isBossSeriesFinished(state.season, state.club, state.boss, state.leagueMovements, state.finaleKind)) return state;
      const selectedCardId = state.selectedCardId;
      if (selectedCardId && !canUseCard(state.inventory, selectedCardId, action.encounterKind)) return state;
      const pendingMatch = {
        ...buildPendingMatch(state, action.encounterKind, selectedCardId),
        outcome: action.outcome,
        opponent: action.opponent,
        matchId: action.matchId,
      };
      const secondLung = REWARDS["segundo-pulmon"].effect;
      const safetyRecovery = pendingMatch.secondLungTriggered && secondLung.type === "physicalSafety" ? secondLung.amount : 0;
      const relicUses = { ...(state.relicUses ?? {}) };
      if (pendingMatch.secondLungTriggered) relicUses["segundo-pulmon"] = state.season;
      if (pendingMatch.restPreparationTriggered && pendingMatch.restPreparationKey !== undefined) relicUses["plan-personalizado"] = pendingMatch.restPreparationKey;
      return {
        ...state,
        physical: clamp(clamp(state.physical + (pendingMatch.physicalRecovery ?? 0) - pendingMatch.physicalCost) + safetyRecovery),
        morale: clamp(state.morale + (pendingMatch.moraleRecovery ?? 0)),
        inventory: selectedCardId ? consumeCard(state.inventory, selectedCardId) : state.inventory,
        relicUses,
        selectedCardId: null,
        pendingMatch,
      };
    }

    case "match/resolved": {
      if (state.pendingMatch?.encounterKind === "boss") return state;
      const resolved = applyMatchResult(state, action.outcome, action.goals, action.assists, action.appearances, action.minutesPlayed, action.wins, action.ovrGain, action.reputationGain, action.streakOutcomes);
      return {
        ...resolved,
        leagueOpponentsPlayed: action.opponents?.length
          ? [...new Set([...state.leagueOpponentsPlayed, ...action.opponents])]
          : state.leagueOpponentsPlayed,
      };
    }

    case "boss/started":
      if (state.boss || state.pendingMatch) return state;
      return { ...state, boss: { round: 0, wins: 0, scores: [] } };

    case "boss/roundResolved": {
      if (!state.boss || state.pendingMatch?.encounterKind !== "boss") return state;
      const resolved = applyMatchResult(state, action.won ? "win" : "loss", action.goals, action.assists, 1, 90);
      const nextRound = state.boss.round + 1;
      const nextWins = state.boss.wins + (action.won ? 1 : 0);
      const stageBoss = getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind);
      const nextBoss = {
        round: nextRound,
        wins: nextWins,
        scores: [...state.boss.scores, action.won ? "win" as const : "loss" as const],
      };
      const continuesSeries = !isBossSeriesFinished(state.season, state.club, nextBoss, state.leagueMovements, state.finaleKind);
      const earnsTitle = nextWins >= stageBoss.requiredWins && stageBoss.awardsTitle;
      const intermissionRecovery = getStageBossBalance(state.season).intermissionPhysicalRecovery;
      const portableRecovery = Math.floor(getRelicEffectAmount(state.inventory, "restPhysicalBonus") / 2);
      const physical = continuesSeries && intermissionRecovery > 0
        ? clamp(resolved.physical + intermissionRecovery + portableRecovery)
        : resolved.physical;
      return {
        ...resolved,
        physical,
        titles: resolved.titles + (earnsTitle ? stageBoss.titlesAwarded ?? 1 : 0),
        boss: nextBoss,
      };
    }

    case "league/promoted": {
      if (state.leagueMovements.some((movement) => movement.promotedClub === action.movement.promotedClub)) return state;
      return {
        ...state,
        leagueMovements: [...state.leagueMovements, action.movement],
        log: appendLog(state.log, `${action.movement.promotedClub} asciende a primera; ${action.movement.relegatedClub} ocupa su plaza en segunda.`),
      };
    }

    case "season/advanced": {
      const moveKind = action.moveKind ?? (action.club === state.club ? "renewal" : "transfer");
      const outcome = getCareerMoveOutcome(state, action.club, moveKind);
      const moveMessage = moveKind === "renewal"
        ? `Renuevas con ${action.club}: conservas la confianza del club.`
        : moveKind === "reserve-promotion"
          ? `Asciendes al primer equipo de ${action.club}: ahora debes ganarte el puesto.`
          : `Fichas por ${action.club}: das un salto deportivo y empiezas de cero ante el entrenador.`;
      return {
        ...state,
        careerHistory: closeCurrentStage(state, "completed", getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind).success),
        stageBaseline: { appearances: state.appearances, minutesPlayed: state.minutesPlayed, goals: state.goals, assists: state.assists, ovr: state.ovr, titles: state.titles },
        leagueOpponentsPlayed: [],
        season: state.season + 1,
        finaleKind: state.season === 4 ? getCareerFinale(state) : state.finaleKind,
        seasonsCompleted: state.seasonsCompleted + 1,
        week: 0,
        club: action.club,
        ovr: outcome.ovr,
        physical: 100,
        morale: outcome.morale,
        clubReputation: outcome.clubReputation,
        oneClubEligible: outcome.oneClubEligible,
        log: appendLog(state.log, moveMessage),
        map: action.map,
        path: [],
        activeNodeId: null,
        boss: null,
        selectedCardId: null,
        pendingMatch: null,
        pendingScoutOffer: [],
        winStreak: 0,
        relicUses: {},
      };
    }

    case "run/finished":
      if (state.finished) return state;
      return {
        ...state,
        finished: true,
        seasonsCompleted: state.seasonsCompleted + (action.completed ? 1 : 0),
        careerHistory: closeCurrentStage(
          state,
          action.completed ? "completed" : "eliminated",
          action.message ?? (action.completed ? "Etapa completada." : "La carrera termina en esta etapa."),
        ),
        log: appendLog(state.log, action.message),
      };

    case "log/added":
      return { ...state, log: appendLog(state.log, action.message) };

    case "decision/resolved": {
      const path = state.activeNodeId ? [...state.path, state.activeNodeId] : state.path;
      return {
        ...state,
        ovr: clamp(state.ovr + (action.ovr ?? 0), 0, 99),
        physical: clamp(state.physical + (action.physical ?? 0)),
        morale: clamp(state.morale + (action.morale ?? 0)),
        clubReputation: clamp(state.clubReputation + (action.reputation ?? 0)),
        activeNodeId: null,
        path,
        week: path.length,
        log: appendLog(state.log, action.message),
      };
    }

    case "node/opened":
      return { ...state, activeNodeId: action.nodeId, pendingScoutOffer: action.scoutOffer ? [...action.scoutOffer] : state.pendingScoutOffer ?? [] };

    case "node/completed": {
      const path = state.activeNodeId ? [...state.path, state.activeNodeId] : state.path;
      return {
        ...state,
        activeNodeId: null,
        pendingScoutOffer: [],
        path,
        week: path.length,
        log: appendLog(state.log, action.message),
      };
    }

    case "stats/changed": {
      return {
        ...state,
        ovr: clamp(state.ovr + (action.ovr ?? 0), 0, 99),
        physical: clamp(state.physical + (action.physical ?? 0)),
        morale: clamp(state.morale + (action.morale ?? 0)),
      };
    }

    default:
      return state;
  }
}
